import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

export default function PaymentResult() {
  const { result } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState(result === "cancel" ? "cancelled" : "polling");
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (result !== "success") return;
    const sid = sp.get("session_id");
    if (!sid) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {}
      if (attempts.current >= 12) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [result, sp]);

  const V = {
    polling: { icon: Loader2, spin: true, color: "text-olive", title: "Confirming your payment…", msg: "Please do not pay again. We are securely verifying your transaction." },
    paid: { icon: CheckCircle2, color: "text-forest", title: "Payment successful", msg: "Your order is confirmed and sent to the manufacturer." },
    cancelled: { icon: XCircle, color: "text-red-500", title: "Payment cancelled", msg: "You cancelled the payment. Your order is still awaiting payment." },
    failed: { icon: XCircle, color: "text-red-500", title: "Payment failed", msg: "Something went wrong. Please try again from your orders." },
    timeout: { icon: Clock, color: "text-amber-500", title: "Taking longer than expected", msg: "We're still checking your transaction. Please do not pay again — check your orders shortly." },
    error: { icon: XCircle, color: "text-red-500", title: "Missing payment reference", msg: "We couldn't find the payment session." },
  }[state];

  return (
    <div className="mx-auto flex max-w-md flex-col items-center py-16 text-center">
      <V.icon className={`h-16 w-16 ${V.color} ${V.spin ? "animate-spin" : ""}`} />
      <h1 className="mt-5 font-heading text-2xl font-semibold text-forest" data-testid="payment-result-title">{V.title}</h1>
      <p className="mt-2 text-sm text-ink/60">{V.msg}</p>
      <div className="mt-6 flex gap-3">
        {state === "paid" && orderId && (
          <Button data-testid="view-order-btn" className="bg-forest text-white hover:bg-forest/90" onClick={() => nav(`/retailer/orders/${orderId}`)}>View order</Button>
        )}
        <Button variant="outline" className="border-forest text-forest hover:bg-psage" onClick={() => nav("/retailer/orders")}>My orders</Button>
      </div>
    </div>
  );
}
